import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import apiService from '../services/apiService';
import AddMemberModal from '../components/modals/AddMemberModal';
import EditMemberModal from '../components/modals/EditMemberModal';
import { 
  Users, 
  ArrowLeft,
  Search,
  Eye,
  Edit,
  Trash2,
  Plus,
  Download,
  UserCheck,
  UserX,
  Mail,
  Phone,
  Building2,
  DollarSign,
  X
} from 'lucide-react';

// Rôles attribuables depuis la gestion des membres d'une zone
const ZONE_ROLE_OPTIONS = [
  { value: 'membre', label: 'Membre' },
  { value: 'tgz', label: 'TGZ' },
  { value: 'sgz', label: 'SGZ' },
  { value: 'mcz', label: 'MCZ' }
];

const ZoneMembersPage = () => {
  const { user, isAuthenticated, isReadOnly } = useAuth();
  const navigate = useNavigate();
  
  // États de base
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFraternity, setFilterFraternity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [fraternities, setFraternities] = useState([]);
  
  // États pour les modals
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  
  // États pour les opérations CRUD
  const [isOperationLoading, setIsOperationLoading] = useState(false);
  const [error, setError] = useState('');

  const loadZoneMembers = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('Chargement des membres de la zone...');

      // Récupérer tous les membres de la zone
      const response = await apiService.get(`/members?zone=${user?.zone}&per_page=100`);
      console.log('Réponse membres zone:', response);

      const membersData = response.data || response.members || [];
      setMembers(membersData);

      // Extraire les fraternités uniques
      const uniqueFraternities = [...new Set(
        membersData
          .map(member => member.fraternity)
          .filter(fraternity => fraternity)
      )].sort();
      
      setFraternities(uniqueFraternities);
      
    } catch (error) {
      console.error('Erreur lors du chargement des membres:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.zone]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!user?.zone) {
      navigate('/dashboard');
      return;
    }

    loadZoneMembers();
  }, [isAuthenticated, user, navigate, loadZoneMembers]);

  // Filtrer les membres
  const filteredMembers = members.filter(member => {
    const matchesSearch = !searchTerm || 
      `${member.first_name} ${member.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.fraternity?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFraternity = !filterFraternity || member.fraternity === filterFraternity;
    const matchesStatus = !filterStatus || member.status === filterStatus;
    
    return matchesSearch && matchesFraternity && matchesStatus;
  });

  const handleViewDetails = (member) => {
    setSelectedMember(member);
    setShowDetailsModal(true);
  };

  const handleAddMember = async (memberData) => {
    try {
      setIsOperationLoading(true);
      setError('');
      
      console.log('Ajout nouveau membre:', memberData);
      
      // Ajouter la zone automatiquement
      const dataWithZone = {
        ...memberData,
        zone: user.zone
      };
      
      const response = await apiService.post('/members', dataWithZone);
      console.log('Réponse ajout membre:', response);
      
      if (response && (response.user || response.message)) {
        console.log('✅ Membre ajouté avec succès');
        setShowAddModal(false);
        await loadZoneMembers(); // Recharger la liste
      } else {
        const errorMsg = response?.error || 'Erreur lors de l\'ajout du membre';
        setError(errorMsg);
        console.error('❌ Erreur ajout membre:', errorMsg);
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout:', error);
      
      // Gestion détaillée des erreurs
      let errorMessage = 'Erreur lors de l\'ajout du membre';
      
      if (error.status === 422 && error.validationErrors) {
        // Erreurs de validation Laravel
        const validationErrors = Object.values(error.validationErrors).flat();
        errorMessage = validationErrors.join(', ');
      } else if (error.data && error.data.message) {
        errorMessage = error.data.message;
      } else if (error.data && error.data.errors) {
        // Autres erreurs de validation
        const validationErrors = Object.values(error.data.errors).flat();
        errorMessage = validationErrors.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsOperationLoading(false);
    }
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setShowEditModal(true);
  };

  const handleUpdateMember = async (memberId, updateData) => {
    try {
      setIsOperationLoading(true);
      setError('');
      
      console.log('Mise à jour membre:', memberId, updateData);
      
      // Vérifier l'authentification
      const token = sessionStorage.getItem('fsf_auth_token') || localStorage.getItem('fsf_auth_token');
      if (!token) {
        setError('Session expirée. Veuillez vous reconnecter.');
        navigate('/login');
        return;
      }
      
      const response = await apiService.put(`/members/${memberId}`, updateData);
      console.log('Réponse mise à jour membre:', response);
      
      if (response && (response.user || response.message)) {
        console.log('✅ Membre mis à jour avec succès');
        setShowEditModal(false);
        setSelectedMember(null);
        await loadZoneMembers(); // Recharger la liste
      } else {
        const errorMsg = response?.error || 'Erreur lors de la mise à jour';
        setError(errorMsg);
        console.error('❌ Erreur mise à jour membre:', errorMsg);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour:', error);
      
      // Gestion détaillée des erreurs
      let errorMessage = 'Erreur lors de la mise à jour du membre';
      
      if (error.status === 401) {
        errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        sessionStorage.removeItem('fsf_auth_token'); localStorage.removeItem('fsf_auth_token');
        navigate('/login');
        return;
      } else if (error.status === 403) {
        errorMessage = 'Vous n\'avez pas les droits pour modifier ce membre.';
      } else if (error.status === 422 && error.validationErrors) {
        // Erreurs de validation Laravel
        const validationErrors = Object.values(error.validationErrors).flat();
        errorMessage = validationErrors.join(', ');
      } else if (error.data && error.data.message) {
        errorMessage = error.data.message;
      } else if (error.data && error.data.errors) {
        // Autres erreurs de validation
        const validationErrors = Object.values(error.data.errors).flat();
        errorMessage = validationErrors.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsOperationLoading(false);
    }
  };

  const handleDeleteMember = async (memberId, memberName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le membre "${memberName}" ?\n\nCette action est irréversible et supprimera également toutes les cotisations associées.`)) {
      return;
    }

    try {
      setError('');
      
      console.log('Suppression membre:', memberId);
      
      const response = await apiService.delete(`/members/${memberId}`);
      console.log('Réponse suppression membre:', response);
      
      if (response && (response.success !== false)) {
        console.log('✅ Membre supprimé avec succès');
        await loadZoneMembers(); // Recharger la liste
      } else {
        setError(response?.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('❌ Erreur lors de la suppression:', error);
      setError(`Erreur lors de la suppression: ${error.message}`);
    }
  };

  const handleExportMembers = () => {
    try {
      console.log('Export des membres:', filteredMembers.length);
      
      // Préparer les données pour l'export
      const exportData = filteredMembers.map(member => ({
        'Prénom': member.first_name || '',
        'Nom': member.last_name || '',
        'Email': member.email || '',
        'Téléphone': member.phone || '',
        'Adresse': member.address || '',
        'Zone': member.zone || '',
        'Fraternité': member.fraternity || '',
        'Statut': member.status || 'actif',
        'Cotisation mensuelle': member.contribution_monthly ? `${member.contribution_monthly.toLocaleString()} F CFA` : '0 F CFA',
        'Cotisation annuelle': member.contribution_monthly ? `${(member.contribution_monthly * 12).toLocaleString()} F CFA` : '0 F CFA',
        'Date d\'adhésion': member.join_date ? new Date(member.join_date).toLocaleDateString('fr-FR') : '',
        'Date de création': member.created_at ? new Date(member.created_at).toLocaleDateString('fr-FR') : '',
      }));

      // Créer le contenu CSV
      const headers = Object.keys(exportData[0] || {});
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => headers.map(header => {
          const value = row[header];
          // Échapper les guillemets et entourer de guillemets si nécessaire
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(','))
      ].join('\n');

      // Créer le fichier et déclencher le téléchargement
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Nom du fichier avec date/heure et zone
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 19).replace(/[T:]/g, '-');
      link.setAttribute('download', `membres-zone-${user.zone}-${dateStr}.csv`);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log(`✅ Export réussi: ${exportData.length} membres exportés`);
    } catch (error) {
      console.error('❌ Erreur lors de l\'export:', error);
      setError('Erreur lors de l\'export. Veuillez réessayer.');
    }
  };

  if (!isAuthenticated || !user?.zone) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Link
                to="/zone-dashboard"
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Retour au Dashboard
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Gestion des Membres - Zone {user.zone}
                </h1>
                <p className="text-sm text-gray-600">
                  Gérer les membres de votre zone par fraternité
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {!isReadOnly && (
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nouveau Membre
              </button>
              )}
              
              <span className="text-sm text-gray-600">
                {user.first_name} {user.last_name}
              </span>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                {user.role?.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Message d'erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <X className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erreur</h3>
                <div className="mt-1 text-sm text-red-700">
                  {error}
                </div>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError('')}
                  className="inline-flex bg-red-50 rounded-md p-1.5 text-red-500 hover:bg-red-100"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Statistiques rapides */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Membres</p>
                <p className="text-2xl font-bold text-gray-900">{members.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <UserCheck className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Actifs</p>
                <p className="text-2xl font-bold text-gray-900">
                  {members.filter(m => m.status === 'actif').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <Building2 className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Fraternités</p>
                <p className="text-2xl font-bold text-gray-900">{fraternities.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <UserX className="h-8 w-8 text-red-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Inactifs</p>
                <p className="text-2xl font-bold text-gray-900">
                  {members.filter(m => m.status !== 'actif').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres et recherche */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Recherche */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher par nom, email, ou fraternité..."
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              
              {/* Filtre par fraternité */}
              <div className="w-full sm:w-64">
                <select
                  value={filterFraternity}
                  onChange={(e) => setFilterFraternity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Toutes les fraternités</option>
                  {fraternities.map((fraternity) => (
                    <option key={fraternity} value={fraternity}>
                      {fraternity}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Filtre par statut */}
              <div className="w-full sm:w-48">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Tous les statuts</option>
                  <option value="actif">Actif</option>
                  <option value="inactif">Inactif</option>
                  <option value="suspendu">Suspendu</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Tableau des membres */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                Membres ({filteredMembers.length})
              </h2>
              <button 
                onClick={handleExportMembers}
                disabled={filteredMembers.length === 0}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4 mr-2" />
                Exporter ({filteredMembers.length})
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Chargement des membres...</p>
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucun membre trouvé avec ces critères</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Membre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fraternité
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cotisation
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 text-justify">
                  {filteredMembers.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {member.first_name} {member.last_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            Membre depuis {member.join_date ? new Date(member.join_date).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {member.fraternity || 'Non assignée'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 mr-2 text-gray-400" />
                            {member.email}
                          </div>
                          {member.phone && (
                            <div className="flex items-center mt-1">
                              <Phone className="h-4 w-4 mr-2 text-gray-400" />
                              {member.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          member.status === 'actif' 
                            ? 'bg-green-100 text-green-800'
                            : member.status === 'inactif'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {member.status || 'actif'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <DollarSign className="h-4 w-4 mr-1 text-gray-400" />
                          <span className="text-sm text-gray-900">
                            {(member.contribution_monthly || 0).toLocaleString()} F CFA
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewDetails(member)}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title="Voir détails"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          
                          {!isReadOnly && (
                          <button
                            onClick={() => handleEditMember(member)}
                            className="text-indigo-600 hover:text-indigo-900 transition-colors"
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          )}
                          
                          {!isReadOnly && (
                          <button
                            onClick={() => handleDeleteMember(member.id, `${member.first_name} ${member.last_name}`)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {/* Modal détails membre */}
      {showDetailsModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Détails du membre
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 text-justify">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">Informations personnelles</h4>
                  <div className="space-y-2">
                    <p><span className="font-medium">Nom:</span> {selectedMember.first_name} {selectedMember.last_name}</p>
                    <p><span className="font-medium">Email:</span> {selectedMember.email}</p>
                    <p><span className="font-medium">Téléphone:</span> {selectedMember.phone || 'Non renseigné'}</p>
                    <p><span className="font-medium">Adresse:</span> {selectedMember.address || 'Non renseignée'}</p>
                    <p><span className="font-medium">Nom du couple:</span> {selectedMember.couple_name || 'Non défini'}</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">Informations organisationnelles</h4>
                  <div className="space-y-2">
                    <p><span className="font-medium">Zone:</span> {selectedMember.zone}</p>
                    <p><span className="font-medium">Fraternité:</span> {selectedMember.fraternity || 'Non assignée'}</p>
                    <p><span className="font-medium">Statut:</span> {selectedMember.status || 'actif'}</p>
                    <p><span className="font-medium">Date d'adhésion:</span> {selectedMember.join_date ? new Date(selectedMember.join_date).toLocaleDateString() : 'Non renseignée'}</p>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Cotisations</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p><span className="font-medium">Cotisation mensuelle:</span> {(selectedMember.contribution_monthly || 0).toLocaleString()} F CFA</p>
                  <p><span className="font-medium">Cotisation annuelle attendue:</span> {((selectedMember.contribution_monthly || 0) * 12).toLocaleString()} F CFA</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal ajout membre */}
      <AddMemberModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setError(''); // Réinitialiser les erreurs
        }}
        onSubmit={handleAddMember}
        fraternities={fraternities}
        isLoading={isOperationLoading}
        roleOptions={ZONE_ROLE_OPTIONS}
      />
      
      {/* Modal modification membre */}
      <EditMemberModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedMember(null);
          setError(''); // Réinitialiser les erreurs
        }}
        onSubmit={handleUpdateMember}
        member={selectedMember}
        fraternities={fraternities}
        isLoading={isOperationLoading}
        roleOptions={ZONE_ROLE_OPTIONS}
      />
    </div>
  );
};

export default ZoneMembersPage;